import { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { theme } from '../../lib/theme';
import { ENTITIES, LEAD_TYPES, LEAD_SOURCES, TEAM_MEMBERS, COMMERCIAL_SUB_TYPES, COMMERCIAL_LEAD_SOURCES, COMMERCIAL_PROPERTY_TYPES, LEASE_TYPES } from '../../lib/constants';
import { supabase } from '../../lib/supabase';

export default function AddLeadForm({ onClose, onSave, teamMembers, defaultCommercial = false }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '',
    type: defaultCommercial ? 'Commercial' : 'Buyer',
    source: defaultCommercial ? 'CoStar' : 'Referral',
    business: 'Brokerage',
    budget: '', timeline: '', notes: '', assignTo: '',
    // Commercial-specific fields
    commercialSubType: '',     // buyer_rep, seller_rep, tenant_rep, landlord_rep
    companyName: '',           // Tenant/buyer company name
    propertySize: '', leaseOrPurchase: defaultCommercial ? '' : 'purchase',
    commercialPropertyType: '',
    desiredLeaseTerms: '', camNnnPreference: '', tenantCount: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Duplicate-detection state. Runs debounced whenever phone or email changes.
  const [duplicates, setDuplicates] = useState([]);
  const [dupChecking, setDupChecking] = useState(false);

  // Debounce the duplicate check so we don't hammer the API on every keystroke
  useEffect(() => {
    const email = (form.email || '').trim().toLowerCase();
    const phone = (form.phone || '').trim();
    if (!email && !phone) { setDuplicates([]); return; }
    // Only query on plausibly-complete values
    const emailLooksReal = email.includes('@') && email.includes('.');
    const phoneLooksReal = phone.replace(/[^\d]/g, '').length >= 10;
    if (!emailLooksReal && !phoneLooksReal) { setDuplicates([]); return; }

    const t = setTimeout(async () => {
      try {
        setDupChecking(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) { setDupChecking(false); return; }
        const r = await fetch('/api/leads?action=check_duplicate', {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailLooksReal ? email : '', phone: phoneLooksReal ? phone : '' }),
        });
        const data = await r.json();
        if (r.ok) setDuplicates(data.duplicates || []);
      } catch { /* non-fatal */ }
      finally { setDupChecking(false); }
    }, 450);
    return () => clearTimeout(t);
  }, [form.email, form.phone]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Map display values to database values
  const typeMap = { 'Buyer': 'buyer', 'Seller': 'seller', 'Renter': 'renter', 'Commercial': 'commercial', 'PM Owner': 'pm_owner', 'Investor': 'investor' };
  const sourceMap = { 'Referral': 'referral', 'Zillow': 'zillow', 'Realtor.com': 'realtor_com', 'Website': 'wix_website', 'Sign Call': 'sign_call', 'Social Media': 'social_media', 'Trulia': 'trulia', 'Redfin': 'redfin', 'LoopNet': 'loopnet', 'CoStar': 'costar', 'Crexi': 'crexi', 'Broker Network': 'broker_network', 'Walk-in': 'walk_in', 'Facebook': 'facebook', 'Other': 'other' };

  const handleSave = async () => {
    if (!form.firstName || !form.phone) {
      setError('First name and phone are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave?.({
        // Database format fields
        first_name: form.firstName,
        last_name: form.lastName,
        phone: form.phone,
        email: form.email || null,
        lead_type: typeMap[form.type] || form.type,
        lead_source: sourceMap[form.source] || form.source,
        budget: form.budget || null,
        timeline: form.timeline || null,
        notes: form.notes || null,
        assigned_to: form.assignTo || null,
        // Commercial structured fields (Migration 047)
        ...(typeMap[form.type] === 'commercial' ? {
          commercial_sub_type: form.commercialSubType || null,
          company_name: form.companyName || null,
          commercial_property_type: form.commercialPropertyType || null,
          desired_sf: form.propertySize ? parseInt(form.propertySize, 10) : null,
          lease_or_purchase: form.leaseOrPurchase || null,
          desired_lease_term: form.desiredLeaseTerms || null,
          cam_nnn_preference: form.camNnnPreference || null,
        } : {}),
        // Legacy mock format fields (for demo mode compatibility)
        name: `${form.firstName} ${form.lastName}`.trim(),
        type: form.type,
        source: form.source,
        entity: form.business,
        status: 'New',
        lead_status: 'new',
        priority: 'high',
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Error saving lead');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, field, required, type = 'text', placeholder }) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>
        {label}{required && ' *'}
      </label>
      <input
        value={form[field]}
        onChange={e => update(field, e.target.value)}
        type={type}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '9px 12px', borderRadius: 8,
          border: `1px solid ${theme.colors.border}`, fontSize: 13,
          outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );

  const Select = ({ label, field, options, valueKey, labelKey }) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>{label}</label>
      <select
        value={form[field]}
        onChange={e => update(field, e.target.value)}
        style={{
          width: '100%', padding: '9px 12px', borderRadius: 8,
          border: `1px solid ${theme.colors.border}`, fontSize: 13,
          background: theme.colors.card,
        }}
      >
        {options.map(o => {
          if (typeof o === 'string') return <option key={o} value={o}>{o}</option>;
          return <option key={o[valueKey]} value={o[valueKey]}>{o[labelKey]}</option>;
        })}
      </select>
    </div>
  );

  // Build assign options: use team members from DB if available, otherwise fallback to constants
  const assignOptions = teamMembers && teamMembers.length > 0
    ? [{ id: '', full_name: '(Unassigned)' }, ...teamMembers]
    : TEAM_MEMBERS.map(name => ({ id: name, full_name: name }));

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200,
    }}>
      <div style={{
        background: theme.colors.card, borderRadius: theme.radius.xl,
        padding: 24, width: 420, maxWidth: '95vw',
        maxHeight: '85vh', overflow: 'auto',
        boxShadow: theme.shadow.lg,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.colors.text }}>Add New Lead</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color={theme.colors.textLight} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '8px 12px', borderRadius: 8, marginBottom: 12,
            background: `${theme.colors.danger}10`, color: theme.colors.danger,
            fontSize: 12, fontWeight: 500,
          }}>{error}</div>
        )}

        {/* Business Entity Toggle */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 6 }}>Business Entity</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {ENTITIES.map(entity => (
              <button
                key={entity}
                onClick={() => update('business', entity)}
                style={{
                  flex: 1, padding: '10px', borderRadius: 10,
                  border: `2px solid ${form.business === entity ? theme.colors.primary : theme.colors.border}`,
                  background: form.business === entity ? `${theme.colors.primary}10` : theme.colors.card,
                  color: form.business === entity ? theme.colors.primary : theme.colors.textLight,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {entity}
              </button>
            ))}
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <Field label="First Name" field="firstName" required placeholder="First name" />
          <Field label="Last Name" field="lastName" placeholder="Last name" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <Field label="Phone" field="phone" required type="tel" placeholder="856-555-0000" />
          <Field label="Email" field="email" type="email" placeholder="Optional" />
        </div>

        {duplicates.length > 0 && (
          <div style={{
            padding: '10px 12px', marginBottom: 10,
            background: '#FEF3C7', border: `1px solid #F59E0B`,
            borderRadius: 8, fontSize: 12, color: '#92400E',
            display: 'flex', gap: 8, alignItems: 'flex-start',
          }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>Possible duplicate{duplicates.length > 1 ? 's' : ''} in your CRM:</strong>
              <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {duplicates.slice(0, 3).map(d => (
                  <div key={d.id} style={{ fontSize: 11 }}>
                    · <strong>{d.first_name} {d.last_name}</strong>
                    {d.email && <> · {d.email}</>}
                    {d.phone && <> · {d.phone}</>}
                    {d.lead_status && <> · <em>{d.lead_status}</em></>}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: '#78350F' }}>
                You can still save this as a new lead — we just want you to know.
              </div>
            </div>
          </div>
        )}
        {dupChecking && !duplicates.length && (
          <div style={{ fontSize: 10, color: theme.colors.textLight, marginBottom: 6, marginTop: -4 }}>
            Checking for duplicates…
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <Select label="Lead Type" field="type" options={LEAD_TYPES} />
          <Select label="Source" field="source" options={form.type === 'Commercial' ? COMMERCIAL_LEAD_SOURCES : LEAD_SOURCES} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <Field label={form.type === 'Commercial' ? 'Budget / Price Range' : 'Budget'} field="budget" placeholder={form.type === 'Commercial' ? '$500K-$2M or $15-$25/sq ft' : '$350K-$400K'} />
          <Field label="Timeline" field="timeline" placeholder={form.type === 'Commercial' ? '6-12 months' : '3 months, ASAP...'} />
        </div>

        {/* Commercial-specific fields */}
        {form.type === 'Commercial' && (
          <div style={{
            padding: '14px 16px', marginBottom: 12, borderRadius: 10,
            background: `${theme.colors.primary}06`, border: `1px solid ${theme.colors.primary}20`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.primary, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Commercial Details
            </div>

            {/* Sub-type picker — most important field, determines pipeline & deal type */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 6 }}>What type of deal is this? *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {COMMERCIAL_SUB_TYPES.map(st => {
                  const active = form.commercialSubType === st.key;
                  return (
                    <button key={st.key} type="button" onClick={() => {
                      update('commercialSubType', st.key);
                      // Auto-set lease or purchase based on sub-type
                      const isLease = st.dealType === 'commercial_lease';
                      update('leaseOrPurchase', isLease ? 'lease' : 'purchase');
                    }} style={{
                      padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      border: active ? `2px solid ${st.color}` : `1px solid ${theme.colors.border}`,
                      background: active ? `${st.color}10` : theme.colors.card,
                      textAlign: 'left', transition: 'all 0.15s',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? st.color : theme.colors.text }}>{st.label}</div>
                      <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>
                        {st.dealType === 'commercial_sale' ? 'Sale deal' : 'Lease deal'}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Company name */}
            <Field label="Company / Entity Name" field="companyName" placeholder="Apex Corp, Chen Holdings LLC..." />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <Field label="Property Size (sq ft)" field="propertySize" placeholder="2,000-5,000 sq ft" />
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Property Type</label>
                <select value={form.commercialPropertyType} onChange={e => update('commercialPropertyType', e.target.value)}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 13, background: theme.colors.card }}>
                  <option value="">Select...</option>
                  {COMMERCIAL_PROPERTY_TYPES.map(pt => (
                    <option key={pt.key} value={pt.key}>{pt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {form.leaseOrPurchase === 'lease' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <Field label="Desired Lease Terms" field="desiredLeaseTerms" placeholder="5-yr NNN, 3+2 option..." />
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Lease Structure</label>
                  <select value={form.camNnnPreference} onChange={e => update('camNnnPreference', e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`, fontSize: 13, background: theme.colors.card }}>
                    <option value="">Select...</option>
                    {LEASE_TYPES.map(lt => (
                      <option key={lt.key} value={lt.key}>{lt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assign To */}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Assign To</label>
          <select
            value={form.assignTo}
            onChange={e => update('assignTo', e.target.value)}
            style={{
              width: '100%', padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${theme.colors.border}`, fontSize: 13,
              background: theme.colors.card,
            }}
          >
            {assignOptions.map(a => (
              <option key={a.id} value={a.id}>{a.full_name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textLight, display: 'block', marginBottom: 3 }}>Notes</label>
          <textarea
            value={form.notes}
            onChange={e => update('notes', e.target.value)}
            rows={3}
            placeholder="Any details about this lead..."
            style={{
              width: '100%', padding: '9px 12px', borderRadius: 8,
              border: `1px solid ${theme.colors.border}`, fontSize: 13,
              outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '10px', borderRadius: 10,
            border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
            color: theme.colors.textLight, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            flex: 1, padding: '10px', borderRadius: 10, border: 'none',
            background: theme.colors.primary, color: 'white',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            opacity: saving ? 0.7 : 1,
          }}>{saving ? 'Saving...' : 'Save Lead'}</button>
        </div>
      </div>
    </div>
  );
}
